import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import './QuizGame.css';
import './KBCMultiplex.css';

const TeacherStudentKBC = ({ 
  gameSession, 
  userRole, 
  userName, 
  userClass,
  userId,
  onExit 
}) => {
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState('waiting');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [studentScore, setStudentScore] = useState(0);
  const [questionActive, setQuestionActive] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [lifelines, setLifelines] = useState({
    fiftyFifty: true,
    skip: true,
    hint: true
  });

  const prizeAmounts = [100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 500000, 1000000];
  const isTeacher = ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(userRole);

  // Audio management
  const [currentAudio, setCurrentAudio] = useState(null);
  const [timerAudio, setTimerAudio] = useState(null);

  const playAudio = (audioFile) => {
    try {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      
      const audio = new Audio(`https://therisingschool.com/Learning%20Games/MP3/${audioFile}`);
      audio.volume = 0.5;
      setCurrentAudio(audio);
      
      audio.play().catch(error => console.log('Audio play failed:', error));
      audio.onended = () => setCurrentAudio(null);
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  };

  const startTimerAudio = () => {
    try {
      if (timerAudio) {
        timerAudio.pause();
        timerAudio.currentTime = 0;
      }
      
      const audio = new Audio('https://therisingschool.com/Learning%20Games/MP3/Timer.mp3');
      audio.volume = 0.3;
      audio.loop = true;
      setTimerAudio(audio);
      
      audio.play().catch(error => console.log('Timer audio play failed:', error));
    } catch (error) {
      console.log('Timer audio not supported:', error);
    }
  };

  const stopTimerAudio = () => {
    if (timerAudio) {
      timerAudio.pause();
      timerAudio.currentTime = 0;
      setTimerAudio(null);
    }
  };

  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    stopTimerAudio();
  };

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io();
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      newSocket.emit('join-game', gameSession.id);
    });

    // Teacher events
    newSocket.on('question-activated', (data) => {
      setQuestionActive(true);
      setCurrentQuestion(data.questionIndex);
      playAudio('Question.mp3');
      setTimeout(() => startTimerAudio(), 1000);
    });

    // Student events
    newSocket.on('student-answered', (data) => {
      setSelectedAnswer(data.answer);
      setShowResult(true);
      setStudentScore(data.score);
      stopTimerAudio();
    });

    newSocket.on('next-question-ready', () => {
      setQuestionActive(false);
      setSelectedAnswer('');
      setShowResult(false);
    });

    newSocket.on('game-finished', (data) => {
      setGameState('finished');
      setStudentScore(data.finalScore);
      stopAudio();
    });

    newSocket.on('player-joined', (data) => {
      if (data.role === 'student') {
        setStudentData(data);
      }
    });

    return () => {
      newSocket.emit('leave-game', gameSession.id);
      newSocket.disconnect();
    };
  }, [gameSession.id]);

  // Load questions and setup
  useEffect(() => {
    if (gameSession.questions && gameSession.questions.length >= 12) {
      setQuestions(gameSession.questions.slice(0, 12));
      setGameState('ready');
    }
  }, [gameSession]);

  // Teacher functions
  const activateQuestion = () => {
    if (socket && isTeacher) {
      socket.emit('activate-question', {
        gameId: gameSession.id,
        questionIndex: currentQuestion,
        question: questions[currentQuestion]
      });
      setQuestionActive(true);
      playAudio('Question.mp3');
      setTimeout(() => startTimerAudio(), 1000);
    }
  };

  const nextQuestion = () => {
    if (socket && isTeacher) {
      if (currentQuestion < questions.length - 1) {
        const nextIndex = currentQuestion + 1;
        setCurrentQuestion(nextIndex);
        socket.emit('prepare-next-question', {
          gameId: gameSession.id,
          questionIndex: nextIndex
        });
        setQuestionActive(false);
        setSelectedAnswer('');
        setShowResult(false);
      } else {
        // Game finished
        socket.emit('finish-game', {
          gameId: gameSession.id,
          finalScore: studentScore
        });
        setGameState('finished');
        stopAudio();
      }
    }
  };

  // Student functions
  const handleAnswerSelect = (answer) => {
    if (!isTeacher && questionActive && !showResult) {
      setSelectedAnswer(answer);
    }
  };

  const submitAnswer = () => {
    if (!isTeacher && selectedAnswer && socket) {
      stopTimerAudio();
      
      const correct = selectedAnswer === questions[currentQuestion].correctAnswer;
      const points = correct ? prizeAmounts[currentQuestion] : 0;
      const newScore = studentScore + points;
      
      setStudentScore(newScore);
      setShowResult(true);

      socket.emit('student-answer', {
        gameId: gameSession.id,
        questionIndex: currentQuestion,
        answer: selectedAnswer,
        isCorrect: correct,
        points: points,
        score: newScore
      });
    }
  };

  const useFiftyFifty = () => {
    if (!lifelines.fiftyFifty || isTeacher) return;
    
    playAudio('Question.mp3');
    
    const currentQ = questions[currentQuestion];
    const wrongAnswers = currentQ.options.filter(opt => opt !== currentQ.correctAnswer);
    const toRemove = wrongAnswers.slice(0, 2);
    
    setQuestions(prev => {
      const updated = [...prev];
      updated[currentQuestion] = {
        ...updated[currentQuestion],
        options: updated[currentQuestion].options.filter(opt => !toRemove.includes(opt))
      };
      return updated;
    });
    
    setLifelines(prev => ({ ...prev, fiftyFifty: false }));
  };

  const useSkip = () => {
    if (!lifelines.skip || isTeacher) return;
    
    playAudio('Question.mp3');
    setLifelines(prev => ({ ...prev, skip: false }));
    
    if (socket) {
      socket.emit('student-skip', {
        gameId: gameSession.id,
        questionIndex: currentQuestion
      });
    }
  };

  const useHint = () => {
    if (!lifelines.hint || isTeacher) return;
    
    playAudio('Question.mp3');
    alert(questions[currentQuestion].hint || "No hint available for this question");
    setLifelines(prev => ({ ...prev, hint: false }));
  };

  if (gameState === 'waiting' || gameState === 'ready') {
    return (
      <div className="quiz-game kbc-multiplex-mode">
        <div className="prize-ladder-container">
          <h2>🎓 Teacher-Student KBC</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {isTeacher ? (
              <>
                <div>You are the Host (Teacher)</div>
                <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                  Control question flow • Give questions one by one
                </div>
                {studentData && (
                  <div style={{ marginTop: '15px', padding: '10px', background: '#f0f9ff', borderRadius: '8px' }}>
                    <div style={{ fontWeight: 'bold' }}>Student: {studentData.name}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Class: {studentData.class}</div>
                  </div>
                )}
                {gameState === 'ready' && (
                  <button 
                    onClick={activateQuestion}
                    style={{
                      marginTop: '15px',
                      padding: '10px 20px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Start First Question
                  </button>
                )}
              </>
            ) : (
              <>
                <div>You are the Contestant (Student)</div>
                <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                  Wait for teacher to give questions
                </div>
                {/* Student Photo */}
                <div style={{ marginTop: '15px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: '#e5e7eb',
                    margin: '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    👨‍🎓
                  </div>
                  <div style={{ marginTop: '8px', fontWeight: 'bold' }}>{userName}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Class: {userClass}</div>
                </div>
              </>
            )}
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="quit-btn">Exit Game</button>
        </div>
      </div>
    );
  }

  if (gameState === 'finished') {
    return (
      <div className="quiz-game kbc-multiplex-mode">
        <div className="prize-ladder-container">
          <h2>🏆 Game Complete!</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: '#e5e7eb',
              margin: '0 auto 15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px'
            }}>
              👨‍🎓
            </div>
            <h3>Final Score: {studentScore.toLocaleString()} Points</h3>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
              Questions Completed: {currentQuestion + 1}/12
            </div>
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="continue-btn">Back to Games</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  if (!currentQ) return <div>Loading...</div>;

  // Teacher View
  if (isTeacher) {
    return (
      <div className="quiz-game kbc-multiplex-mode">
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '15px',
          borderRadius: '8px',
          zIndex: 1000,
          minWidth: '200px'
        }}>
          <div style={{ fontWeight: 'bold', color: '#10b981', marginBottom: '10px' }}>🎓 Teacher Control</div>
          <div>Student Score: {studentScore.toLocaleString()}</div>
          <div>Question: {currentQuestion + 1}/12</div>
          <div style={{ marginTop: '10px' }}>
            {!questionActive ? (
              <button 
                onClick={activateQuestion}
                style={{
                  padding: '8px 16px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Give Question {currentQuestion + 1}
              </button>
            ) : showResult ? (
              <button 
                onClick={nextQuestion}
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                {currentQuestion < questions.length - 1 ? 'Next Question' : 'Finish Game'}
              </button>
            ) : (
              <div style={{ color: '#fbbf24' }}>Student is answering...</div>
            )}
          </div>
        </div>

        <div className="game-header">
          <div className="points-display">
            <div className="coin-animation">
              🪙 {studentScore.toLocaleString()} Points
            </div>
          </div>
          
          <div className="timer-circle multiplex-timer-circle">
            <div className="timer-inner multiplex-timer-inner">
              <span>∞</span>
            </div>
          </div>
          
          <div className="question-counter">
            {currentQuestion + 1}/12
          </div>
        </div>

        <div className="question-section">
          <div className="question-box multiplex-question-box">
            <div className="multiplex-mode-indicator">
              <span className="multiplex-mode-badge">🎓 Teacher View</span>
              <span className="multiplex-custom-badge">⚡ CONTROL MODE</span>
            </div>
            <h3>{currentQ.question}</h3>
          </div>
          
          <div className="options-grid">
            {currentQ.options.map((option, index) => (
              <button
                key={index}
                className={`option-btn ${
                  showResult ? (option === currentQ.correctAnswer ? 'correct' : 
                  selectedAnswer === option ? 'wrong' : '') : ''
                }`}
                disabled={true}
              >
                <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                <span className="option-text">{option}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="game-controls">
          <div style={{ textAlign: 'center', color: 'white', fontSize: '16px' }}>
            Teacher Controls Above ↗️
          </div>
          <button className="quit-btn" onClick={() => { stopAudio(); onExit(); }}>End Game</button>
        </div>
      </div>
    );
  }

  // Student View
  return (
    <div className="quiz-game kbc-multiplex-mode">
      {/* Student Photo Display */}
      <div style={{
        position: 'fixed',
        top: '10px',
        left: '10px',
        background: 'rgba(255,255,255,0.95)',
        padding: '10px',
        borderRadius: '8px',
        zIndex: 1000,
        textAlign: 'center'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: '#e5e7eb',
          margin: '0 auto 5px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px'
        }}>
          👨‍🎓
        </div>
        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{userName}</div>
        <div style={{ fontSize: '10px', color: '#666' }}>Class: {userClass}</div>
      </div>

      <div className="game-header">
        <div className="points-display">
          <div className="coin-animation">
            🪙 {studentScore.toLocaleString()} Points
          </div>
        </div>
        
        <div className="timer-circle multiplex-timer-circle">
          <div className="timer-inner multiplex-timer-inner">
            <span>∞</span>
          </div>
        </div>
        
        <div className="question-counter">
          {currentQuestion + 1}/12
        </div>
      </div>

      <div className="question-section">
        <div className="question-box multiplex-question-box">
          <div className="multiplex-mode-indicator">
            <span className="multiplex-mode-badge">🎓 Student View</span>
            <span className="multiplex-custom-badge">
              {questionActive ? '⚡ ANSWER NOW' : '⏳ WAITING'}
            </span>
          </div>
          {questionActive ? (
            <h3>{currentQ.question}</h3>
          ) : (
            <h3>Waiting for teacher to give the question...</h3>
          )}
        </div>
        
        {questionActive && (
          <div className="options-grid">
            {currentQ.options.map((option, index) => (
              <button
                key={index}
                className={`option-btn ${selectedAnswer === option ? 'selected' : ''} ${
                  showResult ? (option === currentQ.correctAnswer ? 'correct' : 
                  selectedAnswer === option ? 'wrong' : '') : ''
                }`}
                onClick={() => handleAnswerSelect(option)}
                disabled={showResult}
              >
                <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                <span className="option-text">{option}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {questionActive && (
        <div className="game-controls">
          <div className="lifelines">
            <button 
              className={`lifeline-btn ${!lifelines.fiftyFifty ? 'used' : ''}`}
              onClick={useFiftyFifty}
              disabled={!lifelines.fiftyFifty || showResult}
            >
              50:50
            </button>
            <button 
              className={`lifeline-btn ${!lifelines.skip ? 'used' : ''}`}
              onClick={useSkip}
              disabled={!lifelines.skip || showResult}
            >
              Skip
            </button>
            <button 
              className={`lifeline-btn ${!lifelines.hint ? 'used' : ''}`}
              onClick={useHint}
              disabled={!lifelines.hint || showResult}
            >
              Hint
            </button>
          </div>
          
          <div className="action-buttons">
            <button 
              className="submit-btn"
              onClick={submitAnswer}
              disabled={!selectedAnswer || showResult}
            >
              Final Answer
            </button>
            <button className="quit-btn" onClick={() => { stopAudio(); onExit(); }}>Quit Game</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherStudentKBC;